import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// Searchable client/buyer dropdown. When `allowCreate` is false it becomes a strict
// pick-from-list (no inline "+ Create", no free-text commit) — used for locked buyer parties.
// Shared by Yarn Master & Yarn Outward.
const ClientDropdown: React.FC<{
  value: string;
  onChange: (name: string, clientId?: number) => void;
  clients: any[];
  onCreateClient?: (name: string) => void;
  placeholder?: string;
  allowCreate?: boolean;
}> = ({ value, onChange, clients, onCreateClient, placeholder = 'Search buyer...', allowCreate = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropdownPos, setDropdownPos] = useState<{ top?: number; bottom?: number; left: number; width: number }>({ top: 0, left: 0, width: 200 });
  const [openUp, setOpenUp] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) &&
          (!dropdownRef.current || !dropdownRef.current.contains(e.target as Node))) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!isOpen || !inputRef.current) return;
    const reposition = () => {
      if (!inputRef.current) return;
      const rect = inputRef.current.getBoundingClientRect();
      // Flip above the field when the viewport (or an on-screen keyboard) squeezes the space below.
      const shouldOpenUp = window.innerHeight - rect.bottom < 200 && rect.top > 200;
      setOpenUp(shouldOpenUp);
      const width = Math.min(Math.max(rect.width, 250), window.innerWidth - 16);
      setDropdownPos({
        top: shouldOpenUp ? undefined : rect.bottom + 2,
        bottom: shouldOpenUp ? (window.innerHeight - rect.top + 2) : undefined,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
        width,
      });
    };
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [isOpen]);

  const term = isOpen ? search : value;
  const list = Array.isArray(clients) ? clients : []; // defensive: prop may be undefined while the query loads
  const filtered = list.filter((c: any) => c.isActive !== false && c.name.toLowerCase().includes((term || '').toLowerCase()));
  const showCreate = allowCreate && !!onCreateClient && term && !list.some((c: any) => c.name.toLowerCase() === term.toLowerCase());

  return (
    <div className="relative" ref={ref}>
      <input ref={inputRef} type="text" value={isOpen ? search : value}
        onChange={e => { setSearch(e.target.value); if (!isOpen) setIsOpen(true); }}
        onFocus={() => { setIsOpen(true); setSearch(value); }}
        placeholder={placeholder}
        className="w-full px-2.5 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" />
      {isOpen && createPortal(
        <div ref={dropdownRef} className="fixed z-[9999] bg-white border border-[#E1E6EF] rounded-xl shadow-[0px_4px_16px_rgba(0,0,0,0.1)] max-h-48 overflow-y-auto"
          style={{ ...(openUp ? { bottom: dropdownPos.bottom } : { top: dropdownPos.top }), left: dropdownPos.left, width: dropdownPos.width }}>
          {filtered.length > 0 ? filtered.map((c: any) => (
            <button key={c.id} type="button" tabIndex={-1}
              onClick={() => { onChange(c.name, c.id); setSearch(''); setIsOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-[#F8F9FC] transition-colors ${value === c.name ? 'bg-[#F8F9FC] text-[#0055a9] font-medium' : 'text-[#0E131B]'}`}>
              <div className="font-medium">{c.name}</div>
              {c.contact && <div className="text-xs text-[rgba(14,19,27,0.4)]">{c.contact}</div>}
            </button>
          )) : <div className="px-3 py-2 text-xs text-[rgba(14,19,27,0.4)]">No matches</div>}
          {showCreate && (
            <button type="button" tabIndex={-1}
              onClick={() => { onCreateClient?.(term.trim()); onChange(term.trim()); setSearch(''); setIsOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm text-[#0055a9] bg-[#F8F9FC] hover:bg-[#EEF0F6] border-t border-[#E1E6EF] font-medium">
              + Create "{term}"
            </button>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ClientDropdown;
